/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-01-10 00:00:00
 *
 *******************************************************/
// @flow

import React, { Component } from 'react';
import { Field } from 'redux-form';
import { FormLayout, SkeletonBodyText, TextStyle, Link, Stack, Icon } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderAutocomplete } from 'src/components/views/common/autocomplete-hoc';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';

import { getFullAppUrlV2 } from 'src/helpers';
import { CircleAlertMajor } from '@shopify/polaris-icons';
import moment from 'moment';

type Props = {
  loading: boolean,
  enableVariantsDisplay: boolean,
  collections: Array<Object>,
  onChangeCollection: () => void,
  selectedCollections: Array<Object>,
  optionsList: Array<Object>,
  onChangeOption: () => void,
  selectedOptions: Array<Object>,
  merchandisingV2: boolean,
  onOpenNoSupportISW: () => void,
  appliesToInstantSearchWidget: boolean,
  tenantId: Object
};

type ComposedProps = Props & WithI18nProps;

export const OPEN_NO_SUPPORT_ISW_KEY = 'OPEN_NO_SUPPORT_ISW_TIME';

class VariantsDisplayInputForm extends Component<ComposedProps, State> {
  checkShowWarning = () => {
    const { appliesToInstantSearchWidget, merchandisingV2, tenantId } = this.props;
    if (!(merchandisingV2 && appliesToInstantSearchWidget)) {
      return false;
    }

    try {
      const timeOpenNoSupportISW = localStorage.getItem(`${tenantId}:${OPEN_NO_SUPPORT_ISW_KEY}`);
      if (!timeOpenNoSupportISW) {
        return true;
      }
      const dateOpen = new Date(Number(timeOpenNoSupportISW));
      return moment(new Date()).diff(dateOpen, 'day') <= 30;
    } catch (error) {
      return false;
    }
  };

  render() {
    const {
      i18n,
      enableVariantsDisplay,
      loading,
      collections = [],
      selectedCollections = [],
      optionsList = [],
      selectedOptions = [],
      merchandisingV2 = false,
      onOpenNoSupportISW
    } = this.props;

    const filterSettingsUrl = getFullAppUrlV2('/filter-settings');

    return loading ? (
      <FormLayout>
        <SkeletonBodyText lines={5} />
      </FormLayout>
    ) : (
      <FormLayout>
        <p>
          {i18n.translate('AdminHTML.VariantsDisplay.Form.description', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.VariantsDisplay.Form.description_t1')}
              </TextStyle>
            ),
            t2: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.VariantsDisplay.Form.description_t2')}
              </TextStyle>
            ),
            link: (
              <Link external url={filterSettingsUrl}>
                {i18n.translate('AdminHTML.VariantsDisplay.Form.description_link')}
              </Link>
            )
          })}
        </p>
        <Field
          name="enable"
          component={RenderToggleSwitch}
          label={i18n.translate('AdminHTML.VariantsDisplay.Form.enableVariantsDisplay')}
        />

        {enableVariantsDisplay && (
          <div>
            {/* Options */}
            <Field
              name="options"
              component={RenderAutocomplete}
              label={i18n.translate('AdminHTML.VariantsDisplay.Form.optionsLabel')}
              options={optionsList}
              forceInit={true}
              havingAllOption={true}
              value={selectedOptions}
              placeholder={i18n.translate('AdminHTML.VariantsDisplay.Form.optionsPlaceholder')}
              tooltip={i18n.translate('AdminHTML.VariantsDisplay.Form.optionsTooltip', {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.VariantsDisplay.Form.optionsTooltip_t1')}
                  </TextStyle>
                ),
                t2: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.VariantsDisplay.Form.optionsTooltip_t2')}
                  </TextStyle>
                )
              })}
              onChange={this.props.onChangeOption}
            />

            {/* Collections */}
            <Field
              name="collections"
              component={RenderAutocomplete}
              label={i18n.translate('AdminHTML.VariantsDisplay.Form.collectionsLabel')}
              options={collections}
              forceInit={true}
              havingAllOption={true}
              value={selectedCollections}
              placeholder={i18n.translate('AdminHTML.VariantsDisplay.Form.collectionsPlaceholder')}
              onChange={this.props.onChangeCollection}
            />

            {/* Search function display on */}
            <div style={{ paddingTop: '5px' }}>
              <TextStyle>
                {i18n.translate('AdminHTML.VariantsDisplay.Form.searchFunctionDisplayOn')}
              </TextStyle>

              <div style={{ paddingTop: '4px' }}>
                <Stack distribution="fill">
                  <Field
                    name="appliesToSearchPage"
                    component={RenderCheckbox}
                    label={i18n.translate('AdminHTML.VariantsDisplay.Form.searchResultPage')}
                  />
                  {!merchandisingV2 && (
                    <Field
                      name="appliesToInstantSearchWidget"
                      component={RenderCheckbox}
                      label={i18n.translate('AdminHTML.VariantsDisplay.Form.instantSearchResult')}
                    />
                  )}
                </Stack>
              </div>
            </div>
            {this.checkShowWarning() && (
              <div className="noSupportISW">
                <span className="icon">
                  <Icon color="critical" source={CircleAlertMajor} />
                </span>
                <p className="text">
                  {`${i18n.translate('AdminHTML.VariantsDisplay.Modal.noSupportISW_detail')} `}
                </p>
                <Link onClick={onOpenNoSupportISW}>
                  {i18n.translate('AdminHTML.Common.Action.learnMore')}
                </Link>
              </div>
            )}
          </div>
        )}
      </FormLayout>
    );
  }
}
export default withI18n()(VariantsDisplayInputForm);
